import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { RejectButton } from 'src/components/Buttons/RejectButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { ButtonProps, Stack } from 'src/core/ui';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { SALE_CANCELLATION_REASON_CID } from 'src/utils/constants/contentIdMaps';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ActivityLogClient,
  ApiException,
  Feature,
  Permission,
  SaleCancellationReason,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

import * as styles from '../SaleDetails.css';
import { checkboxStyles } from './LaunchRejectSale.css';

export const LaunchRejectSale = ({
  saleId,
  marketplaceSaleId,
  variant,
  iconOnlyMode,
  disabled,
}: {
  saleId: number;
  marketplaceSaleId?: string | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [saleCancellationReason, setSaleCancellationReason] =
    useState<SaleCancellationReason | null>(null);
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig, loginContext } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const queryClient = useQueryClient();

  const hasAdminPermission = useUserHasAnyOfPermissions(
    Permission.SellerAccount_Admin,
    Permission.Inventory_ManageAdminHolds
  );
  // POS-9312: the hold-on-rejection behaviour is gated by flag 100652
  const hasHoldOnRejectFeature = useUserHasFeature(
    Feature.HoldInventorySaleManuallyRejected
  );

  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();
  const [shouldAdminHold, setShouldAdminHold] = useState<boolean>(
    // Prefill according to account setting but allow user to override
    !!loginContext?.user?.activeAccount?.sellerAccountSettings
      ?.adminHoldIndefinitelyForCancelledSales
  );
  const onToggleAdminHold = useCallback(
    () => setShouldAdminHold((prev) => !prev),
    []
  );

  const rejectSale = useCallback(() => {
    setIsLoading(true);
    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);

        const result = await client.rejectSale(
          saleId,
          false,
          saleCancellationReason,
          hasHoldOnRejectFeature ? shouldAdminHold : null
        );
        if (!isSuccess(result)) {
          showErrorDialog(
            'SaleClient.rejectSale',
            {
              message: result.message ?? genericError,
              status: result.status!,
            } as ApiException,
            {
              trackErrorData: { saleId },
            }
          );
        } else {
          await setActivePosEntity(saleId, marketplaceSaleId, true);
          // POS-8987: a reject sets the sale's cancellationReason, which the group
          // payment flow uses to exclude the sale's lines from coverages. Invalidate
          // the group sales queries so a follow-up group payment reads the sale as
          // cancelled — otherwise a stale coverage for a now-cancelled line is
          // rejected by the backend ("MarketplacePaymentLineId ... not found").
          await queryClient.invalidateQueries({
            queryKey: ['getSalesInSalesGroup'],
          });
          await queryClient.invalidateQueries({
            queryKey: [SALES_GROUP_QUERY_KEY],
          });
        }
      },
      (error) => {
        showErrorDialog('SaleClient.rejectSale', error, {
          trackErrorData: { saleId },
        });
      },
      () => {
        setIsLoading(false);
        closeDialog();
      }
    );
  }, [
    activeAccountWebClientConfig,
    closeDialog,
    genericError,
    marketplaceSaleId,
    queryClient,
    saleCancellationReason,
    saleId,
    setActivePosEntity,
    hasHoldOnRejectFeature,
    shouldAdminHold,
    showErrorDialog,
  ]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      tryInvokeApi(
        async () => {
          await new ActivityLogClient(
            activeAccountWebClientConfig
          ).trackEntityView(saleId, ActionOutboxEntityType.Sale);
        },
        (error) => {
          trackError('ActivityLogClient.trackEntityView', error, { saleId });
        }
      );
      setSaleCancellationReason(null);
      launchDialog();
    },
    [launchDialog, activeAccountWebClientConfig, saleId, trackError]
  );

  return (
    <div>
      <RejectButton
        onClick={onLaunchDialog}
        variant={variant}
        disabled={disabled || isLoading}
        iconOnlyMode={iconOnlyMode}
        textContentId={ContentId.RejectSale}
        titleContentId={ContentId.RejectSale}
      />
      <ConfirmDialog
        {...dialogProps}
        onOkay={rejectSale}
        disabled={isLoading}
        isLoading={isLoading}
        headerText={<Content id={ContentId.RejectSale} />}
        bodyText={
          <Stack direction="column" gap="l">
            <Stack
              direction="column"
              gap="s"
              className={styles.cancellationReasonWrapper}
            >
              <Content id={ContentId.CancellationReason} />
              <PosEnumSelect
                className={styles.cancellationReasonSelector}
                valueOptionsContent={SALE_CANCELLATION_REASON_CID}
                value={saleCancellationReason}
                onChange={setSaleCancellationReason}
              />
            </Stack>
            <Content id={ContentId.RejectSaleWarning} />
            {hasAdminPermission && hasHoldOnRejectFeature && (
              <Checkbox
                className={checkboxStyles}
                labelPosition="right"
                checked={shouldAdminHold}
                onChange={onToggleAdminHold}
                label={<Content id={ContentId.PutSaleListingOnHold} />}
              />
            )}
          </Stack>
        }
      />
    </div>
  );
};
